import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    TextField,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    Button,
    useTheme,
    Stack,
    FormControl,
    InputLabel,
    Radio,
    RadioGroup,
    FormLabel,
    Snackbar,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { dataPrincipalsService, CreateDataPrincipalRequest } from '../../services/dataPrincipals-service';
import { config } from '../../config/config';
import { useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';

// List of 23 DPDP Act languages
const DPDP_LANGUAGES: { code: string; label: string }[] = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'Hindi' },
    { code: 'bn', label: 'Bengali' },
    { code: 'te', label: 'Telugu' },
    { code: 'mr', label: 'Marathi' },
    { code: 'ta', label: 'Tamil' },
    { code: 'gu', label: 'Gujarati' },
    { code: 'ur', label: 'Urdu' },
    { code: 'kn', label: 'Kannada' },
    { code: 'or', label: 'Odia' },
    { code: 'ml', label: 'Malayalam' },
    { code: 'pa', label: 'Punjabi' },
    { code: 'as', label: 'Assamese' },
    { code: 'ma', label: 'Maithili' },
    { code: 'sa', label: 'Sanskrit' },
    { code: 'sd', label: 'Sindhi' },
    { code: 'ks', label: 'Kashmiri' },
    { code: 'ne', label: 'Nepali' },
    { code: 'ko', label: 'Konkani' },
    { code: 'do', label: 'Dogri' },
    { code: 'mt', label: 'Manipuri' },
    { code: 'bo', label: 'Bodo' },
    { code: 'sa', label: 'Santhali' },
];

// Country codes for phone numbers (India first as default)
const COUNTRY_CODES: { code: string; dialCode: string; name: string }[] = [
    { code: 'IN', dialCode: '+91', name: 'India' },
    { code: 'US', dialCode: '+1', name: 'United States' },
    { code: 'GB', dialCode: '+44', name: 'United Kingdom' },
    { code: 'CA', dialCode: '+1', name: 'Canada' },
    { code: 'AU', dialCode: '+61', name: 'Australia' },
    { code: 'DE', dialCode: '+49', name: 'Germany' },
    { code: 'FR', dialCode: '+33', name: 'France' },
    { code: 'IT', dialCode: '+39', name: 'Italy' },
    { code: 'ES', dialCode: '+34', name: 'Spain' },
    { code: 'NL', dialCode: '+31', name: 'Netherlands' },
    { code: 'BE', dialCode: '+32', name: 'Belgium' },
    { code: 'CH', dialCode: '+41', name: 'Switzerland' },
    { code: 'AT', dialCode: '+43', name: 'Austria' },
    { code: 'SE', dialCode: '+46', name: 'Sweden' },
    { code: 'NO', dialCode: '+47', name: 'Norway' },
    { code: 'DK', dialCode: '+45', name: 'Denmark' },
    { code: 'FI', dialCode: '+358', name: 'Finland' },
    { code: 'PL', dialCode: '+48', name: 'Poland' },
    { code: 'CZ', dialCode: '+420', name: 'Czech Republic' },
    { code: 'HU', dialCode: '+36', name: 'Hungary' },
    { code: 'RO', dialCode: '+40', name: 'Romania' },
    { code: 'BG', dialCode: '+359', name: 'Bulgaria' },
    { code: 'GR', dialCode: '+30', name: 'Greece' },
    { code: 'PT', dialCode: '+351', name: 'Portugal' },
    { code: 'IE', dialCode: '+353', name: 'Ireland' },
    { code: 'LU', dialCode: '+352', name: 'Luxembourg' },
    { code: 'MT', dialCode: '+356', name: 'Malta' },
    { code: 'CY', dialCode: '+357', name: 'Cyprus' },
    { code: 'EE', dialCode: '+372', name: 'Estonia' },
    { code: 'LV', dialCode: '+371', name: 'Latvia' },
    { code: 'LT', dialCode: '+370', name: 'Lithuania' },
    { code: 'SI', dialCode: '+386', name: 'Slovenia' },
    { code: 'SK', dialCode: '+421', name: 'Slovakia' },
    { code: 'HR', dialCode: '+385', name: 'Croatia' },
    { code: 'JP', dialCode: '+81', name: 'Japan' },
    { code: 'KR', dialCode: '+82', name: 'South Korea' },
    { code: 'CN', dialCode: '+86', name: 'China' },
    { code: 'SG', dialCode: '+65', name: 'Singapore' },
    { code: 'HK', dialCode: '+852', name: 'Hong Kong' },
    { code: 'TW', dialCode: '+886', name: 'Taiwan' },
    { code: 'TH', dialCode: '+66', name: 'Thailand' },
    { code: 'MY', dialCode: '+60', name: 'Malaysia' },
    { code: 'ID', dialCode: '+62', name: 'Indonesia' },
    { code: 'PH', dialCode: '+63', name: 'Philippines' },
    { code: 'VN', dialCode: '+84', name: 'Vietnam' },
    { code: 'BR', dialCode: '+55', name: 'Brazil' },
    { code: 'MX', dialCode: '+52', name: 'Mexico' },
    { code: 'AR', dialCode: '+54', name: 'Argentina' },
    { code: 'CL', dialCode: '+56', name: 'Chile' },
    { code: 'CO', dialCode: '+57', name: 'Colombia' },
    { code: 'PE', dialCode: '+51', name: 'Peru' },
    { code: 'ZA', dialCode: '+27', name: 'South Africa' },
    { code: 'EG', dialCode: '+20', name: 'Egypt' },
    { code: 'NG', dialCode: '+234', name: 'Nigeria' },
    { code: 'KE', dialCode: '+254', name: 'Kenya' },
    { code: 'MA', dialCode: '+212', name: 'Morocco' },
    { code: 'TN', dialCode: '+216', name: 'Tunisia' },
    { code: 'DZ', dialCode: '+213', name: 'Algeria' },
    { code: 'LY', dialCode: '+218', name: 'Libya' },
    { code: 'SD', dialCode: '+249', name: 'Sudan' },
    { code: 'ET', dialCode: '+251', name: 'Ethiopia' },
    { code: 'GH', dialCode: '+233', name: 'Ghana' },
    { code: 'UG', dialCode: '+256', name: 'Uganda' },
    { code: 'TZ', dialCode: '+255', name: 'Tanzania' },
    { code: 'ZM', dialCode: '+260', name: 'Zambia' },
    { code: 'ZW', dialCode: '+263', name: 'Zimbabwe' },
    { code: 'BW', dialCode: '+267', name: 'Botswana' },
    { code: 'NA', dialCode: '+264', name: 'Namibia' },
    { code: 'MW', dialCode: '+265', name: 'Malawi' },
    { code: 'MZ', dialCode: '+258', name: 'Mozambique' },
    { code: 'MG', dialCode: '+261', name: 'Madagascar' },
    { code: 'MU', dialCode: '+230', name: 'Mauritius' },
    { code: 'SC', dialCode: '+248', name: 'Seychelles' },
    { code: 'RU', dialCode: '+7', name: 'Russia' },
    { code: 'UA', dialCode: '+380', name: 'Ukraine' },
    { code: 'BY', dialCode: '+375', name: 'Belarus' },
    { code: 'KZ', dialCode: '+7', name: 'Kazakhstan' },
    { code: 'UZ', dialCode: '+998', name: 'Uzbekistan' },
    { code: 'KG', dialCode: '+996', name: 'Kyrgyzstan' },
    { code: 'TJ', dialCode: '+992', name: 'Tajikistan' },
    { code: 'TM', dialCode: '+993', name: 'Turkmenistan' },
    { code: 'AF', dialCode: '+93', name: 'Afghanistan' },
    { code: 'PK', dialCode: '+92', name: 'Pakistan' },
    { code: 'BD', dialCode: '+880', name: 'Bangladesh' },
    { code: 'LK', dialCode: '+94', name: 'Sri Lanka' },
    { code: 'NP', dialCode: '+977', name: 'Nepal' },
    { code: 'BT', dialCode: '+975', name: 'Bhutan' },
    { code: 'MV', dialCode: '+960', name: 'Maldives' },
    { code: 'MM', dialCode: '+95', name: 'Myanmar' },
    { code: 'LA', dialCode: '+856', name: 'Laos' },
    { code: 'KH', dialCode: '+855', name: 'Cambodia' },
];

interface AddDataPrincipalPanelProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const AddDataPrincipalPanel: React.FC<AddDataPrincipalPanelProps> = ({ open, onClose, onSave }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [identifier, setIdentifier] = useState('');
    const [identifierType, setIdentifierType] = useState<'EMAIL' | 'PHONE'>('EMAIL');
    const [countryCode, setCountryCode] = useState('+91'); // Default to India
    const [externalIdentifier, setExternalIdentifier] = useState('');
    const [defaultLanguageCode, setDefaultLanguageCode] = useState('en');
    const [isTest, setIsTest] = useState(false);
    const [isMinor, setIsMinor] = useState(false);
    const [parentIdentifier, setParentIdentifier] = useState('');
    const [parentIdentifierType, setParentIdentifierType] = useState<'EMAIL' | 'PHONE'>('EMAIL');
    const [parentCountryCode, setParentCountryCode] = useState('+91'); // Default to India

    // Validation errors
    const [identifierError, setIdentifierError] = useState('');
    const [parentIdentifierError, setParentIdentifierError] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const isSubmittingRef = useRef(false);

    // Existing DP details (for duplicate detection)
    const [existingDP, setExistingDP] = useState<{
        dpId: number;
        identifierDisplay: string;
        externalIdentifier: string | null;
        identifierType: string;
        errorMessage?: string;
    } | null>(null);

    // Reset form and submit guard when panel opens/closes
    useEffect(() => {
        if (open) {
            isSubmittingRef.current = false;
            setIdentifier('');
            setIdentifierType('EMAIL');
            setCountryCode('+91');
            setExternalIdentifier('');
            setDefaultLanguageCode('en');
            setIsTest(true);
            setIsMinor(false);
            setParentIdentifier('');
            setParentIdentifierType('EMAIL');
            setParentCountryCode('+91');
            setIdentifierError('');
            setParentIdentifierError('');
            setError(null);
            setExistingDP(null);
        }
    }, [open]);

    // Clear existing DP when identifier or external identifier changes
    useEffect(() => {
        setExistingDP(null);
    }, [identifier, identifierType, externalIdentifier]);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    // Country-specific phone number validation rules
    const getPhoneValidationRules = (dialCode: string): { minLength: number; maxLength: number; pattern?: RegExp; example?: string } => {
        const rules: { [key: string]: { minLength: number; maxLength: number; pattern?: RegExp; example?: string } } = {
            '+91': { minLength: 10, maxLength: 10, pattern: /^[6-9]\d{9}$/, example: '9876543210' }, // India: 10 digits, starts with 6-9
            '+1': { minLength: 10, maxLength: 10, example: '1234567890' }, // US/Canada: 10 digits
            '+44': { minLength: 10, maxLength: 10, example: '7123456789' }, // UK: 10 digits
            '+61': { minLength: 9, maxLength: 9, example: '412345678' }, // Australia: 9 digits
            '+49': { minLength: 10, maxLength: 11, example: '15123456789' }, // Germany: 10-11 digits
            '+33': { minLength: 9, maxLength: 9, example: '612345678' }, // France: 9 digits
            '+39': { minLength: 9, maxLength: 10, example: '3123456789' }, // Italy: 9-10 digits
            '+34': { minLength: 9, maxLength: 9, example: '612345678' }, // Spain: 9 digits
            '+31': { minLength: 9, maxLength: 9, example: '612345678' }, // Netherlands: 9 digits
            '+86': { minLength: 11, maxLength: 11, example: '13812345678' }, // China: 11 digits
            '+81': { minLength: 10, maxLength: 11, example: '9012345678' }, // Japan: 10-11 digits
            '+82': { minLength: 9, maxLength: 10, example: '1012345678' }, // South Korea: 9-10 digits
            '+65': { minLength: 8, maxLength: 8, example: '91234567' }, // Singapore: 8 digits
            '+92': { minLength: 10, maxLength: 10, example: '3001234567' }, // Pakistan: 10 digits
            '+880': { minLength: 10, maxLength: 10, example: '1712345678' }, // Bangladesh: 10 digits
            '+94': { minLength: 9, maxLength: 9, example: '712345678' }, // Sri Lanka: 9 digits
            '+977': { minLength: 10, maxLength: 10, example: '9841234567' }, // Nepal: 10 digits
            '+971': { minLength: 9, maxLength: 9, example: '501234567' }, // UAE: 9 digits
            '+966': { minLength: 9, maxLength: 9, example: '501234567' }, // Saudi Arabia: 9 digits
            '+27': { minLength: 9, maxLength: 9, example: '821234567' }, // South Africa: 9 digits
            '+234': { minLength: 10, maxLength: 10, example: '8021234567' }, // Nigeria: 10 digits
            '+254': { minLength: 9, maxLength: 9, example: '712345678' }, // Kenya: 9 digits
            '+55': { minLength: 10, maxLength: 11, example: '11987654321' }, // Brazil: 10-11 digits
            '+52': { minLength: 10, maxLength: 10, example: '5512345678' }, // Mexico: 10 digits
        };

        return rules[dialCode] || { minLength: 6, maxLength: 15 }; // Default: 6-15 digits for other countries
    };

    const validatePhone = (phone: string, dialCode: string): { isValid: boolean; error?: string } => {
        // Remove spaces, dashes, parentheses, and other non-digit characters
        const cleanedPhone = phone.trim().replace(/[\s\-().]/g, '');

        // Check if empty
        if (!cleanedPhone) {
            return { isValid: false, error: 'Phone number is required' };
        }

        // Check if contains only digits
        if (!/^\d+$/.test(cleanedPhone)) {
            return { isValid: false, error: 'Phone number must contain only digits' };
        }

        // Get validation rules for the country
        const rules = getPhoneValidationRules(dialCode);
        const phoneLength = cleanedPhone.length;

        // Check length
        if (phoneLength < rules.minLength) {
            return { 
                isValid: false, 
                error: `Phone number must be at least ${rules.minLength} digits${rules.example ? ` (e.g., ${rules.example})` : ''}` 
            };
        }

        if (phoneLength > rules.maxLength) {
            return { 
                isValid: false, 
                error: `Phone number must be at most ${rules.maxLength} digits${rules.example ? ` (e.g., ${rules.example})` : ''}` 
            };
        }

        // Check pattern if available (e.g., India mobile numbers should start with 6-9)
        if (rules.pattern && !rules.pattern.test(cleanedPhone)) {
            if (dialCode === '+91') {
                return { 
                    isValid: false, 
                    error: 'Indian mobile numbers must start with 6, 7, 8, or 9' 
                };
            }
            return { 
                isValid: false, 
                error: `Invalid phone number format${rules.example ? ` (e.g., ${rules.example})` : ''}` 
            };
        }

        return { isValid: true };
    };

    const validateForm = (): boolean => {
        let isValid = true;

        // Validate identifier
        if (!identifier.trim()) {
            setIdentifierError('Identifier is required');
            isValid = false;
        } else if (identifierType === 'EMAIL' && !validateEmail(identifier)) {
            setIdentifierError('Invalid email format');
            isValid = false;
        } else if (identifierType === 'PHONE') {
            const phoneValidation = validatePhone(identifier, countryCode);
            if (!phoneValidation.isValid) {
                setIdentifierError(phoneValidation.error || 'Invalid phone format');
                isValid = false;
            } else {
                setIdentifierError('');
            }
        } else {
            setIdentifierError('');
        }

        // Validate parent identifier if minor
        if (isMinor) {
            if (!parentIdentifier.trim()) {
                setParentIdentifierError('Parent identifier is required for minor data principals');
                isValid = false;
            } else if (parentIdentifierType === 'EMAIL' && !validateEmail(parentIdentifier)) {
                setParentIdentifierError('Invalid parent email format');
                isValid = false;
            } else if (parentIdentifierType === 'PHONE') {
                const phoneValidation = validatePhone(parentIdentifier, parentCountryCode);
                if (!phoneValidation.isValid) {
                    setParentIdentifierError(phoneValidation.error || 'Invalid parent phone format');
                    isValid = false;
                } else {
                    setParentIdentifierError('');
                }
            } else {
                setParentIdentifierError('');
            }
        } else {
            setParentIdentifierError('');
        }

        return isValid;
    };

    const handleSave = async () => {
        if (isSubmittingRef.current) return;
        setError(null);

        if (!validateForm()) {
            return;
        }

        isSubmittingRef.current = true;
        setIsSaving(true);

        try {
            // Combine country code with phone number if identifier type is PHONE
            let finalIdentifier = identifier.trim();
            if (identifierType === 'PHONE') {
                // Remove any existing country code from the phone number
                // Only remove if it matches the selected country code or common patterns
                let cleanedPhone = identifier.trim().replace(/\s/g, '');
                
                // Remove the selected country code if present at the start
                if (cleanedPhone.startsWith(countryCode)) {
                    cleanedPhone = cleanedPhone.substring(countryCode.length);
                }
                // Also handle cases where user might have entered just the digits of country code
                else if (cleanedPhone.startsWith(countryCode.replace('+', ''))) {
                    cleanedPhone = cleanedPhone.substring(countryCode.replace('+', '').length);
                }
                // Remove leading + if present (but keep the number as-is otherwise)
                else if (cleanedPhone.startsWith('+')) {
                    // Only remove + if followed by digits that could be a country code
                    // Otherwise, assume it's part of the number format and remove it
                    cleanedPhone = cleanedPhone.replace(/^\+/, '');
                }
                
                finalIdentifier = `${countryCode}${cleanedPhone}`;
            }

            let finalParentIdentifier: string | null = null;
            if (isMinor && parentIdentifier.trim() && parentIdentifierType === 'PHONE') {
                // Remove any existing country code from the parent phone number
                // Only remove if it matches the selected country code or common patterns
                let cleanedParentPhone = parentIdentifier.trim().replace(/\s/g, '');
                
                // Remove the selected country code if present at the start
                if (cleanedParentPhone.startsWith(parentCountryCode)) {
                    cleanedParentPhone = cleanedParentPhone.substring(parentCountryCode.length);
                }
                // Also handle cases where user might have entered just the digits of country code
                else if (cleanedParentPhone.startsWith(parentCountryCode.replace('+', ''))) {
                    cleanedParentPhone = cleanedParentPhone.substring(parentCountryCode.replace('+', '').length);
                }
                // Remove leading + if present (but keep the number as-is otherwise)
                else if (cleanedParentPhone.startsWith('+')) {
                    cleanedParentPhone = cleanedParentPhone.replace(/^\+/, '');
                }
                
                finalParentIdentifier = `${parentCountryCode}${cleanedParentPhone}`;
            } else if (isMinor && parentIdentifier.trim()) {
                finalParentIdentifier = parentIdentifier.trim();
            }

            const request: CreateDataPrincipalRequest = {
                identifier: finalIdentifier,
                identifierType,
                externalIdentifier: externalIdentifier.trim() || null,
                defaultLanguageCode: defaultLanguageCode || null,
                isTest,
                isMinor: isMinor || false,
                parentIdentifier: finalParentIdentifier,
                parentIdentifierType: isMinor && parentIdentifier.trim() ? parentIdentifierType : null,
            };

            await dataPrincipalsService.createDataPrincipal(request);
            onSave?.();
            onClose();
        } catch (error: any) {
            console.error('Error creating data principal:', error);
            
            // Handle 409 Conflict (duplicate)
            if (error.response?.status === 409 && error.response?.data?.existingDP) {
                const existingDPData = error.response.data.existingDP;
                const errorMessage = error.response?.data?.error || '';
                // Store the error message with existingDP to show appropriate message
                setExistingDP({
                    ...existingDPData,
                    errorMessage: errorMessage
                });
                setError(null); // Don't show snackbar for duplicate, show Alert instead
            } else {
                const errorMessage = error.response?.data?.error || error.message || 'Failed to create data principal';
                setError(errorMessage);
                setExistingDP(null);
            }
        } finally {
            isSubmittingRef.current = false;
            setIsSaving(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '600px',
                    bgcolor: theme.palette.background.paper,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Add Data Principal
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Duplicate Alert */}
                {existingDP && (
                    <Box sx={{ px: 3, pt: 2 }}>
                        <Alert 
                            severity="warning" 
                            sx={{ mb: 2 }}
                            onClose={() => setExistingDP(null)}
                        >
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                                {existingDP.errorMessage || `A data principal with this ${existingDP.identifierType?.toLowerCase() || 'identifier'} already exists`}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                                <Typography variant="body2">
                                    <strong>DP ID:</strong>{' '}
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={() => {
                                            navigate(`/workbench/data-principals/${existingDP.dpId}`);
                                            onClose();
                                        }}
                                        sx={{
                                            color: theme.palette.primary.main,
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: theme.palette.primary.dark
                                            }
                                        }}
                                    >
                                        {existingDP.dpId}
                                    </Link>
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Identifier:</strong> {existingDP.identifierDisplay}
                                </Typography>
                                {existingDP.externalIdentifier && (
                                    <Typography variant="body2">
                                        <strong>External ID:</strong> {existingDP.externalIdentifier}
                                    </Typography>
                                )}
                            </Box>
                        </Alert>
                    </Box>
                )}

                {/* Form Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                    <Stack spacing={3}>
                        {/* Identifier Type */}
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                Identifier Type <span style={{ color: 'red' }}>*</span>
                            </FormLabel>
                            <RadioGroup
                                row
                                value={identifierType}
                                onChange={(e) => {
                                    setIdentifierType(e.target.value as 'EMAIL' | 'PHONE');
                                    setIdentifier('');
                                    setIdentifierError('');
                                }}
                            >
                                <FormControlLabel value="EMAIL" control={<Radio />} label="Email" />
                                <FormControlLabel value="PHONE" control={<Radio />} label="Phone" />
                            </RadioGroup>
                        </FormControl>

                        {/* Identifier */}
                        {identifierType === 'PHONE' ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel id="country-code-label">Country Code</InputLabel>
                                    <Select
                                        labelId="country-code-label"
                                        label="Country Code"
                                        value={countryCode}
                                        onChange={(e) => {
                                            setCountryCode(e.target.value);
                                            // Re-validate phone number when country code changes
                                            if (identifier) {
                                                const validation = validatePhone(identifier, e.target.value);
                                                setIdentifierError(validation.isValid ? '' : (validation.error || ''));
                                            }
                                        }}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                display: 'flex',
                                                alignItems: 'center',
                                            }
                                        }}
                                    >
                                        {COUNTRY_CODES.map((country) => (
                                            <MenuItem key={country.code} value={country.dialCode}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 50 }}>
                                                        {country.dialCode}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {country.name}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Phone Number"
                                    fullWidth
                                    size="small"
                                    required
                                    value={identifier}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                                        setIdentifier(value);
                                        // Real-time validation
                                        if (value) {
                                            const validation = validatePhone(value, countryCode);
                                            setIdentifierError(validation.isValid ? '' : (validation.error || ''));
                                        } else {
                                            setIdentifierError('');
                                        }
                                    }}
                                    onBlur={() => {
                                        // Validate on blur for better UX
                                        if (identifier) {
                                            const validation = validatePhone(identifier, countryCode);
                                            setIdentifierError(validation.isValid ? '' : (validation.error || ''));
                                        }
                                    }}
                                    error={!!identifierError}
                                    helperText={identifierError || `Enter phone number without country code (e.g., ${getPhoneValidationRules(countryCode).example || '1234567890'})`}
                                    placeholder={getPhoneValidationRules(countryCode).example || '1234567890'}
                                    inputProps={{ 
                                        maxLength: getPhoneValidationRules(countryCode).maxLength,
                                        inputMode: 'numeric'
                                    }}
                                />
                            </Box>
                        ) : (
                            <TextField
                                label="Email Address"
                                fullWidth
                                size="small"
                                required
                                value={identifier}
                                onChange={(e) => {
                                    setIdentifier(e.target.value);
                                    setIdentifierError('');
                                }}
                                error={!!identifierError}
                                helperText={identifierError}
                                placeholder="example@email.com"
                            />
                        )}

                        {/* External Identifier */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TextField
                                label="External Identifier"
                                fullWidth
                                size="small"
                                value={externalIdentifier}
                                onChange={(e) => setExternalIdentifier(e.target.value)}
                                helperText="Optional: External system identifier (e.g., Patient ID, Customer ID)"
                            />
                            <Tooltip title="External identifier from your system (e.g., billing system, CRM)">
                                <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.secondary, cursor: 'pointer' }} />
                            </Tooltip>
                        </Box>

                        {/* Default Language */}
                        <FormControl fullWidth size="small">
                            <InputLabel id="language-label">Default Language</InputLabel>
                            <Select
                                labelId="language-label"
                                label="Default Language"
                                value={defaultLanguageCode}
                                onChange={(e) => setDefaultLanguageCode(e.target.value)}
                            >
                                {DPDP_LANGUAGES.map(lang => (
                                    <MenuItem key={lang.code} value={lang.code}>
                                        {lang.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Is Test Data */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isTest}
                                    onChange={(e) => setIsTest(e.target.checked)}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    Is Test Data
                                    <Tooltip title="Test data stores identifiers directly in the database. Production data stores identifiers in a secure vault.">
                                        <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.secondary, cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                            }
                        />

                        {/* Is Minor */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isMinor}
                                    onChange={(e) => {
                                        setIsMinor(e.target.checked);
                                        if (!e.target.checked) {
                                            setParentIdentifier('');
                                            setParentIdentifierError('');
                                        }
                                    }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    Is Minor
                                    <Tooltip title="Enable this if the data principal is a minor. Parent identifier will be required.">
                                        <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.secondary, cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                            }
                        />

                        {/* Parent Identifier Fields (conditional) */}
                        {isMinor && (
                            <>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                        Parent Identifier Type <span style={{ color: 'red' }}>*</span>
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        value={parentIdentifierType}
                                        onChange={(e) => {
                                            setParentIdentifierType(e.target.value as 'EMAIL' | 'PHONE');
                                            setParentIdentifier('');
                                            setParentIdentifierError('');
                                        }}
                                    >
                                        <FormControlLabel value="EMAIL" control={<Radio />} label="Email" />
                                        <FormControlLabel value="PHONE" control={<Radio />} label="Phone" />
                                    </RadioGroup>
                                </FormControl>

                                {parentIdentifierType === 'PHONE' ? (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <FormControl size="small" sx={{ minWidth: 140 }}>
                                            <InputLabel id="parent-country-code-label">Country Code</InputLabel>
                                            <Select
                                                labelId="parent-country-code-label"
                                                label="Country Code"
                                                value={parentCountryCode}
                                                onChange={(e) => {
                                                    setParentCountryCode(e.target.value);
                                                    // Re-validate parent phone number when country code changes
                                                    if (parentIdentifier) {
                                                        const validation = validatePhone(parentIdentifier, e.target.value);
                                                        setParentIdentifierError(validation.isValid ? '' : (validation.error || ''));
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiSelect-select': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }
                                                }}
                                            >
                                                {COUNTRY_CODES.map((country) => (
                                                    <MenuItem key={country.code} value={country.dialCode}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 50 }}>
                                                                {country.dialCode}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                {country.name}
                                                            </Typography>
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label="Parent Phone Number"
                                            fullWidth
                                            size="small"
                                            required
                                            value={parentIdentifier}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                                                setParentIdentifier(value);
                                                // Real-time validation
                                                if (value) {
                                                    const validation = validatePhone(value, parentCountryCode);
                                                    setParentIdentifierError(validation.isValid ? '' : (validation.error || ''));
                                                } else {
                                                    setParentIdentifierError('');
                                                }
                                            }}
                                            onBlur={() => {
                                                // Validate on blur for better UX
                                                if (parentIdentifier) {
                                                    const validation = validatePhone(parentIdentifier, parentCountryCode);
                                                    setParentIdentifierError(validation.isValid ? '' : (validation.error || ''));
                                                }
                                            }}
                                            error={!!parentIdentifierError}
                                            helperText={parentIdentifierError || `Enter phone number without country code (e.g., ${getPhoneValidationRules(parentCountryCode).example || '1234567890'})`}
                                            placeholder={getPhoneValidationRules(parentCountryCode).example || '1234567890'}
                                            inputProps={{ 
                                                maxLength: getPhoneValidationRules(parentCountryCode).maxLength,
                                                inputMode: 'numeric'
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <TextField
                                        label="Parent Email Address"
                                        fullWidth
                                        size="small"
                                        required
                                        value={parentIdentifier}
                                        onChange={(e) => {
                                            setParentIdentifier(e.target.value);
                                            setParentIdentifierError('');
                                        }}
                                        error={!!parentIdentifierError}
                                        helperText={parentIdentifierError}
                                        placeholder="parent@email.com"
                                    />
                                )}
                            </>
                        )}
                    </Stack>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1
                }}>
                    <Button onClick={onClose} sx={{ color: theme.palette.text.primary }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '&:hover': { bgcolor: theme.palette.primary.dark }
                        }}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>

            {/* Error Snackbar */}
            <Snackbar
                open={!!error}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={() => setError(null)}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Drawer>
    );
};

export default AddDataPrincipalPanel;

